import {
  Component,
  OnInit,
  OnDestroy,
  SecurityContext,
  inject,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject, take, takeUntil } from 'rxjs';
import cytoscape, { Core, NodeDefinition, EdgeDefinition } from 'cytoscape';
import Prism from 'prismjs';
import 'prismjs/components/prism-json';
import DOMPurify from 'dompurify';
import fcose from 'cytoscape-fcose';

try {
  if (typeof fcose === 'function') {
    cytoscape.use(fcose);
  } else if (fcose && typeof (fcose as any).default === 'function') {
    cytoscape.use((fcose as any).default);
  } else {
    console.error('[References] Unexpected fcose type:', typeof fcose, fcose);
  }
} catch (e) {
  console.error('[References] Failed to register fcose layout:', e);
}

import { PlanReviewerService } from '../../../../shared/plan-reviewer.service';
import { EnvironmentService } from '../../../../shared/environment.service';
import { FeedbackService, SubmitFeedbackResponse } from '../../../../services/feedback.service';
import { TabService } from '../../../../shared/tab.service';
import {
  FindingReportDialogComponent,
  FindingReportDialogData,
  FindingReportDialogResult,
  ROOT_CAUSE_CATEGORIES,
} from './finding-report-dialog/finding-report-dialog.component';
import { ArchitecturalPlanPage } from '../../../../shared/models/architectural-plan-page.model';
import {
  GetCrossReferenceGraphResponse,
  GetElementRegistryResponse,
  GetCrossExaminationResponse,
  CrossExaminationFinding as ProtoCrossExaminationFinding,
  BuildingElement as ProtoBuildingElement,
} from '../../../../../generated.commonjs/cross_reference_pb';

export interface SharedElement {
  elementId: string;
  canonicalName: string;
  elementType: string;
  identityClass: string | null;
  confidence: string | null;
  matchingBasis: string | null;
  appearances: Array<{
    fileId: string;
    page: number;
    projectionType: string;
    description: string;
  }>;
}

export interface ExaminationFinding {
  sourceFileId: string;
  sourcePage: number;
  targetFileId: string;
  targetPage: number;
  edgeType: string;
  edgeReference: string;
  findingType: string;
  severity: string;
  confidence: number;
  detail: string;
  sourceExcerpt: string | null;
  targetExcerpt: string | null;
  recommendation: string | null;
}

const FINDING_SEVERITY_ICONS: Record<string, string> = {
  error: 'error',
  warning: 'warning',
  info: 'check_circle',
};

const FINDING_SEVERITY_COLORS: Record<string, string> = {
  error: '#c62828',
  warning: '#f57c00',
  info: '#388e3c',
};

const EDGE_COLORS: Record<string, string> = {
  'detail-callout': '#1976d2',
  'schedule-ref': '#388e3c',
  'project-data': '#f57c00',
  'general-notes': '#7b1fa2',
  'implicit-context': '#757575',
  'cross-file': '#c62828',
};

/**
 *
 */
export function estimateLabelWidth(label: string): number {
  if (!label) return 60;
  const lines = label.split('\n');
  const maxLineLen = Math.max(...lines.map(l => l.length));
  return Math.max(80, Math.min(220, maxLineLen * 7.5 + 30));
}

/**
 *
 */
export function estimateLabelHeight(label: string): number {
  if (!label) return 30;
  const lines = label.split('\n');
  return lines.length * 16 + 20;
}

/** Displays an interactive Cytoscape.js graph of cross-page references (Issue #1106). */
@Component({
  selector: 'app-references',
  templateUrl: './references.component.html',
  styleUrls: ['./references.component.scss'],
  imports: [
    CommonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatProgressBarModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatExpansionModule,
    MatTooltipModule,
  ],
})
export class ReferencesComponent implements OnInit, OnDestroy {
  @ViewChild('cyContainer', { static: false }) cyContainer!: ElementRef;

  private planReviewerService = inject(PlanReviewerService);
  private environmentService = inject(EnvironmentService);
  private feedbackService = inject(FeedbackService);
  private tabService = inject(TabService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private sanitizer = inject(DomSanitizer);
  private destroy$ = new Subject<void>();

  activePage: ArchitecturalPlanPage | null = null;
  loading = false;
  rebuilding = false;
  rebuildingRegistry = false;
  graphExists = false;
  nodeCount = 0;
  edgeCount = 0;
  builtAt = '';
  viewMode: 'graph' | 'data' = 'graph';
  graphPerspective: 'cross-refs' | 'elements' | 'combined' = 'cross-refs';
  rawDataHtml: SafeHtml = this.sanitizer.bypassSecurityTrustHtml('');
  rawDataText = '';
  selectedEdgeInfo: {
    type: string;
    color: string;
    reference: string;
    sourceLabel: string;
    targetLabel: string;
  } | null = null;
  legendItems = Object.entries(EDGE_COLORS).map(([type, color]) => ({ type, color }));

  sharedElements: SharedElement[] = [];
  registryExists = false;
  registryBuiltAt = '';
  selectedElement: SharedElement | null = null;
  elementsOnCurrentPage: SharedElement[] = [];
  elementsDrawerOpen = false;
  drawerWidth = 420;

  // Cross-examination state (#1103)
  examFindings: ExaminationFinding[] = [];
  examExists = false;
  examBuiltAt = '';
  examEdgesExamined = 0;
  examContradictions = 0;
  examDiscrepancies = 0;
  examMatches = 0;
  examMissingTargets = 0;
  runningExam = false;
  reportingFinding: ExaminationFinding | null = null;
  findingsDrawerOpen = false;
  findingsOnCurrentPage: ExaminationFinding[] = [];

  private resizing = false;
  private resizeMoveHandler: ((e: MouseEvent) => void) | null = null;
  private resizeUpHandler: (() => void) | null = null;

  private cy: Core | null = null;
  graphResponse: GetCrossReferenceGraphResponse | null = null;
  private registryResponse: GetElementRegistryResponse | null = null;
  loadedProjectId: string | null = null;
  private pendingRenderTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.tabService.activePlanPage$.pipe(takeUntil(this.destroy$)).subscribe(page => {
      const previousPage = this.activePage;
      this.activePage = page;

      if (page) {
        const projectId = page.getArchitecturalPlanId();
        if (projectId && projectId !== this.loadedProjectId) {
          this.loadGraph(projectId);
        } else if (this.cy) {
          this.updateCurrentPageHighlight();
        }
      }

      if (!previousPage && page && !this.graphResponse && !this.loading) {
        const projectId = page.getArchitecturalPlanId();
        if (projectId) {
          this.loadGraph(projectId);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelPendingRender();
    this.cleanupResize();
    if (this.cy) {
      this.cy.destroy();
      this.cy = null;
    }
  }

  private cancelPendingRender(): void {
    if (this.pendingRenderTimer !== null) {
      clearTimeout(this.pendingRenderTimer);
      this.pendingRenderTimer = null;
    }
  }

  private scheduleRender(): void {
    this.cancelPendingRender();
    this.pendingRenderTimer = setTimeout(() => {
      this.pendingRenderTimer = null;
      this.renderGraph();
    }, 0);
  }

  private updateCurrentPageHighlight(): void {
    if (!this.cy || !this.activePage) return;
    const currentKey = `${this.extractFileId(this.activePage)}-${this.activePage.getPageNumber()}`;
    this.cy.nodes().removeClass('current');
    const currentNode = this.cy.getElementById(currentKey);
    if (currentNode.length) {
      currentNode.addClass('current');
    }
    this.updateElementsOnCurrentPage();
    this.updateFindingsOnCurrentPage();
  }

  private async loadGraph(projectId: string): Promise<void> {
    this.loading = true;
    this.loadedProjectId = projectId;
    this.cdr.detectChanges();

    const [graphResp, registryResp, examResp] = await Promise.all([
      this.planReviewerService.getCrossReferenceGraph(projectId),
      this.planReviewerService.getElementRegistry(projectId),
      this.planReviewerService.getCrossExamination(projectId),
    ]);

    this.loading = false;

    // Registry is independent of the graph — always process it
    this.registryResponse = registryResp;
    if (registryResp && registryResp.getExists()) {
      this.registryExists = true;
      this.registryBuiltAt = registryResp.getBuiltAt();
      this.sharedElements = this.parseSharedElements(registryResp);
      this.updateElementsOnCurrentPage();
    } else {
      this.registryExists = false;
      this.registryBuiltAt = '';
      this.sharedElements = [];
      this.selectedElement = null;
      this.updateElementsOnCurrentPage();
    }

    // Cross-examination is independent — always process it
    this.loadExamination(examResp);

    if (!graphResp || !graphResp.getExists()) {
      this.graphExists = false;
      this.graphResponse = null;
      this.rawDataText = '';
      this.rawDataHtml = this.highlightJson('');
      this.cdr.detectChanges();
      return;
    }

    this.graphExists = true;
    this.graphResponse = graphResp;
    this.nodeCount = graphResp.getNodesList().length;
    this.edgeCount = graphResp.getEdgesList().length;
    this.builtAt = graphResp.getBuiltAt();

    if (this.registryExists && this.graphPerspective !== 'cross-refs') {
      this.elementsDrawerOpen = true;
    }

    this.refreshRawData();
    this.cdr.detectChanges();

    if (this.viewMode === 'graph') {
      this.scheduleRender();
    }
  }

  private parseSharedElements(response: GetElementRegistryResponse): SharedElement[] {
    return response.getElementsList().map((el: ProtoBuildingElement) => ({
      elementId: el.getElementId(),
      canonicalName: el.getCanonicalName(),
      elementType: el.getElementType(),
      identityClass: el.getIdentityClass() || null,
      confidence: el.getConfidence() || null,
      matchingBasis: el.getMatchingBasis() || null,
      appearances: el.getAppearancesList().map(a => ({
        fileId: a.getFileId(),
        page: a.getPage(),
        projectionType: a.getProjectionType(),
        description: a.getDescription(),
      })),
    }));
  }

  private updateElementsOnCurrentPage(): void {
    if (!this.activePage || this.sharedElements.length === 0) {
      this.elementsOnCurrentPage = [];
      return;
    }
    const fileId = this.extractFileId(this.activePage);
    const pageNum = this.activePage.getPageNumber();
    this.elementsOnCurrentPage = this.sharedElements.filter(el =>
      el.appearances.some(a => a.fileId === fileId && a.page === pageNum)
    );
  }

  private loadExamination(examResp: GetCrossExaminationResponse | null): void {
    if (examResp && examResp.getExists()) {
      this.examExists = true;
      this.examBuiltAt = examResp.getBuiltAt();
      this.examEdgesExamined = examResp.getEdgesExamined();
      this.examContradictions = examResp.getContradictions();
      this.examDiscrepancies = examResp.getDiscrepancies();
      this.examMatches = examResp.getMatches();
      this.examMissingTargets = examResp.getMissingTargets();
      this.examFindings = examResp.getFindingsList().map((f: ProtoCrossExaminationFinding) => this.parseFinding(f));
      this.updateFindingsOnCurrentPage();
    } else {
      this.examExists = false;
      this.examBuiltAt = '';
      this.examFindings = [];
      this.findingsOnCurrentPage = [];
      this.examEdgesExamined = 0;
      this.examContradictions = 0;
      this.examDiscrepancies = 0;
      this.examMatches = 0;
      this.examMissingTargets = 0;
    }
  }

  private parseFinding(f: ProtoCrossExaminationFinding): ExaminationFinding {
    const edge = f.getEdge();
    const src = edge?.getSource();
    const tgt = edge?.getTarget();
    return {
      sourceFileId: src?.getFileId() || '',
      sourcePage: src?.getPage() || 0,
      targetFileId: tgt?.getFileId() || '',
      targetPage: tgt?.getPage() || 0,
      edgeType: edge?.getType() || '',
      edgeReference: edge?.getReference() || '',
      findingType: f.getFindingType(),
      severity: f.getSeverity(),
      confidence: f.getConfidence(),
      detail: f.getDetail(),
      sourceExcerpt: f.getSourceExcerpt() || null,
      targetExcerpt: f.getTargetExcerpt() || null,
      recommendation: f.getRecommendation() || null,
    };
  }

  private updateFindingsOnCurrentPage(): void {
    if (!this.activePage || this.examFindings.length === 0) {
      this.findingsOnCurrentPage = [];
      return;
    }
    const fileId = this.extractFileId(this.activePage);
    const pageNum = this.activePage.getPageNumber();
    this.findingsOnCurrentPage = this.examFindings.filter(
      f =>
        (f.sourceFileId === fileId && f.sourcePage === pageNum) ||
        (f.targetFileId === fileId && f.targetPage === pageNum)
    );
  }

  async runCrossExamination(): Promise<void> {
    if (!this.loadedProjectId || this.runningExam) return;
    this.runningExam = true;
    this.cdr.detectChanges();
    try {
      const result = await this.planReviewerService.runCrossExamination(this.loadedProjectId);
      if (result.success) {
        const displayId =
          result.taskId && result.taskId.length > 8 ? result.taskId.substring(0, 8) + '…' : result.taskId || 'unknown';
        this.snackBar.open(`Cross-examination started (Task: ${displayId})`, 'OK', {
          duration: 5000,
        });
      } else {
        this.snackBar.open(`Cross-examination failed: ${result.message}`, 'OK', { duration: 5000 });
      }
    } catch {
      this.snackBar.open('Failed to start cross-examination', 'OK', { duration: 5000 });
    } finally {
      this.runningExam = false;
      this.cdr.detectChanges();
    }
  }

  getSeverityIcon(severity: string): string {
    return FINDING_SEVERITY_ICONS[severity] || 'help';
  }

  getSeverityColor(severity: string): string {
    return FINDING_SEVERITY_COLORS[severity] || '#757575';
  }

  getEnvironmentLabel(): string {
    const projectId = this.environmentService.getFirebaseConfig().projectId || '';
    const lastDash = projectId.lastIndexOf('-');
    if (lastDash === -1) {
      return 'prod';
    }
    return projectId.substring(lastDash + 1);
  }

  async copyFindingJson(finding: ExaminationFinding): Promise<void> {
    const payload = {
      environment: this.getEnvironmentLabel(),
      projectId: this.loadedProjectId,
      builtAt: this.examBuiltAt,
      finding,
    };
    await this.copyToClipboard(JSON.stringify(payload, null, 2), 'Finding JSON copied');
  }

  async copyAllFindingsJson(): Promise<void> {
    const payload = {
      environment: this.getEnvironmentLabel(),
      projectId: this.loadedProjectId,
      builtAt: this.examBuiltAt,
      edgesExamined: this.examEdgesExamined,
      summary: {
        contradictions: this.examContradictions,
        discrepancies: this.examDiscrepancies,
        matches: this.examMatches,
        missingTargets: this.examMissingTargets,
        total: this.examFindings.length,
      },
      findings: this.examFindings,
    };
    await this.copyToClipboard(JSON.stringify(payload, null, 2), 'All findings copied');
  }

  async copyDiagnosticMetadata(): Promise<void> {
    const meta = [
      `Environment: ${this.getEnvironmentLabel()}`,
      `Project ID: ${this.loadedProjectId || 'unknown'}`,
      `Built at: ${this.examBuiltAt || 'N/A'}`,
      `Edges examined: ${this.examEdgesExamined}`,
      `Findings: ${this.examFindings.length} (${this.examContradictions} contradictions, ${this.examDiscrepancies} discrepancies, ${this.examMatches} matches, ${this.examMissingTargets} missing)`,
    ].join('\n');
    await this.copyToClipboard(meta, 'Metadata copied');
  }

  private async copyToClipboard(text: string, successMessage: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.snackBar.open(successMessage, 'OK', { duration: 3000 });
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      this.snackBar.open('Copy failed — check browser permissions', 'OK', { duration: 4000 });
    }
  }

  reportFinding(finding: ExaminationFinding): void {
    const dialogData: FindingReportDialogData = {
      finding,
      environmentLabel: this.getEnvironmentLabel(),
      projectId: this.loadedProjectId || 'unknown',
      builtAt: this.examBuiltAt,
      edgesExamined: this.examEdgesExamined,
    };

    const dialogRef = this.dialog.open(FindingReportDialogComponent, {
      data: dialogData,
      width: '560px',
      maxHeight: '90vh',
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: FindingReportDialogResult | undefined) => {
        if (!result) return;
        this.submitFindingReport(finding, result);
      });
  }

  private submitFindingReport(finding: ExaminationFinding, result: FindingReportDialogResult): void {
    this.reportingFinding = finding;
    this.cdr.detectChanges();
    this.snackBar.open('Filing issue...', undefined, { duration: 0 });

    const rootCauseLabel = ROOT_CAUSE_CATEGORIES.find(c => c.value === result.rootCause)?.label || result.rootCause;

    const findingJson = JSON.stringify(finding, null, 2);
    const description = [
      '## Cross-Examination Finding Report',
      '',
      '### User Analysis',
      result.analysis,
      '',
      `### Root Cause Category`,
      `**${rootCauseLabel}** (\`${result.rootCause}\`)`,
      '',
      '### Environment',
      `- **Env**: ${this.getEnvironmentLabel()}`,
      `- **Project ID**: ${this.loadedProjectId || 'unknown'}`,
      `- **Built at**: ${this.examBuiltAt || 'N/A'}`,
      `- **Edges examined**: ${this.examEdgesExamined}`,
      '',
      '### Finding Details',
      `- **Type**: ${finding.findingType}`,
      `- **Severity**: ${finding.severity}`,
      `- **Confidence**: ${Math.round(finding.confidence * 100)}%`,
      `- **Edge**: ${finding.edgeType} (${finding.edgeReference || 'no reference'})`,
      `- **Pages**: p${finding.sourcePage} → p${finding.targetPage}`,
      '',
      `**Detail**: ${finding.detail}`,
      '',
      ...(finding.sourceExcerpt ? [`**Source excerpt**: ${finding.sourceExcerpt}`, ''] : []),
      ...(finding.targetExcerpt ? [`**Target excerpt**: ${finding.targetExcerpt}`, ''] : []),
      ...(finding.recommendation ? [`**Recommendation**: ${finding.recommendation}`, ''] : []),
      '<details><summary>Raw JSON</summary>',
      '',
      '```json',
      findingJson,
      '```',
      '',
      '</details>',
      '',
      '### Root Cause Taxonomy Reference',
      ...ROOT_CAUSE_CATEGORIES.map(c => `- ${c.value}: ${c.description}`),
    ].join('\n');

    const uiState = JSON.stringify({
      finding,
      examMetadata: {
        builtAt: this.examBuiltAt,
        edgesExamined: this.examEdgesExamined,
        contradictions: this.examContradictions,
        discrepancies: this.examDiscrepancies,
        matches: this.examMatches,
        missingTargets: this.examMissingTargets,
        totalFindings: this.examFindings.length,
      },
    });

    this.feedbackService
      .submitFeedback({
        description,
        category: 'BUG',
        pageUrl: window.location.href,
        uiStateJson: uiState,
      })
      .pipe(take(1))
      .subscribe({
        next: (response: SubmitFeedbackResponse) => {
          this.reportingFinding = null;
          this.cdr.detectChanges();
          this.snackBar
            .open(`Issue #${response.issueNumber} created`, 'View Issue', {
              duration: 8000,
            })
            .onAction()
            .subscribe(() => {
              if (response.issueUrl?.startsWith('https://')) {
                window.open(response.issueUrl, '_blank');
              }
            });
        },
        error: (err: { status: number; message: string }) => {
          this.reportingFinding = null;
          this.cdr.detectChanges();
          console.error('[References] Failed to file finding issue:', err);
          this.snackBar.open(err.message || 'Failed to file issue', 'Dismiss', {
            duration: 8000,
          });
        },
      });
  }

  private renderGraph(): void {
    if (!this.graphResponse || !this.cyContainer) return;

    const { nodes, edges } = this.buildElements(this.graphResponse);

    if (this.cy) {
      this.cy.destroy();
    }

    try {
      this.cy = cytoscape({
        container: this.cyContainer.nativeElement,
        style: [
          {
            selector: 'node',
            style: {
              label: 'data(label)',
              'text-wrap': 'wrap' as any,
              'text-max-width': '180px',
              'font-size': '11px',
              'text-valign': 'center',
              'text-halign': 'center',
              'background-color': '#e0e0e0',
              color: '#212121',
              width: 'data(nodeWidth)' as any,
              height: 'data(nodeHeight)' as any,
              'border-width': 2,
              'border-color': '#bdbdbd',
              shape: 'round-rectangle',
            },
          },
          {
            selector: 'node.current',
            style: {
              'background-color': '#1976d2',
              color: '#ffffff',
              'border-color': '#0d47a1',
              'border-width': 3,
              'font-weight': 'bold' as any,
              'font-size': '12px',
            },
          },
          {
            selector: 'node.element-highlight',
            style: {
              'border-color': '#e65100',
              'border-width': 4,
              'background-color': '#fff3e0',
              color: '#e65100',
            },
          },
          {
            selector: 'edge',
            style: {
              width: 2,
              'line-color': 'data(color)' as any,
              'target-arrow-color': 'data(color)' as any,
              'target-arrow-shape': 'triangle',
              'curve-style': 'bezier',
              'control-point-step-size': 60,
              label: 'data(edgeLabel)' as any,
              'font-size': '9px',
              color: '#616161',
              'text-rotation': 'autorotate' as any,
              'text-margin-y': -10,
              'text-background-color': '#ffffff',
              'text-background-opacity': 0.85,
              'text-background-padding': 2 as any,
            },
          },
          {
            selector: 'edge:selected',
            style: {
              width: 4,
              'line-color': '#ff5722',
              'target-arrow-color': '#ff5722',
            },
          },
        ],
        minZoom: 0.3,
        maxZoom: 3,
        wheelSensitivity: 0.3,
        textureOnViewport: true,
        hideEdgesOnViewport: true,
      } as any);

      this.cy.add(nodes);
      this.cy.add(edges);

      this.cy
        .layout({
          name: 'fcose',
          animate: 'end',
          animationDuration: 400,
          quality: 'default',
          nodeRepulsion: () => 80000,
          idealEdgeLength: () => 220,
          edgeElasticity: () => 0.45,
          gravity: 0.15,
          gravityRange: 1.5,
          numIter: 300,
          padding: 50,
          nodeDimensionsIncludeLabels: true,
          uniformNodeDimensions: false,
          packComponents: true,
          tile: true,
          tilingPaddingVertical: 30,
          tilingPaddingHorizontal: 30,
          nodeSeparation: 120,
        } as any)
        .run();

      this.cy.on('tap', 'node', (event: cytoscape.EventObjectNode) => {
        const nodeData = event.target.data();
        if (nodeData.fileId && nodeData.pageNumber) {
          this.navigateToPage(nodeData.fileId, nodeData.pageNumber);
        }
      });

      this.cy.on('tap', 'edge', (event: cytoscape.EventObjectEdge) => {
        const edgeData = event.target.data();
        this.selectedEdgeInfo = {
          type: edgeData.edgeLabel || edgeData.type || '',
          color: edgeData.color || '#9e9e9e',
          reference: edgeData.reference,
          sourceLabel: edgeData.sourceLabel || '',
          targetLabel: edgeData.targetLabel || '',
        };
        this.cdr.detectChanges();
      });

      this.cy.on('tap', (event: cytoscape.EventObject) => {
        if (event.target === this.cy) {
          this.selectedEdgeInfo = null;
          this.cdr.detectChanges();
        }
      });
    } catch (e) {
      console.error('[References] Cytoscape init error:', e);
    }
  }

  buildElements(response: GetCrossReferenceGraphResponse): {
    nodes: NodeDefinition[];
    edges: EdgeDefinition[];
  } {
    const nodes: NodeDefinition[] = [];
    const edges: EdgeDefinition[] = [];
    const currentPageKey = this.activePage
      ? `${this.extractFileId(this.activePage)}-${this.activePage.getPageNumber()}`
      : '';

    const elemCountByPage = new Map<string, number>();
    for (const el of this.sharedElements) {
      for (const a of el.appearances) {
        const pk = `${a.fileId}-${a.page}`;
        elemCountByPage.set(pk, (elemCountByPage.get(pk) ?? 0) + 1);
      }
    }

    for (const node of response.getNodesList()) {
      const key = `${node.getFileId()}-${node.getPage()}`;
      const title = node.getTitle() || `Page ${node.getPage()}`;
      const sheetId = node.getSheetId();
      const badge = sheetId ? `Page ${node.getPage()} · Sheet ${sheetId}` : `Page ${node.getPage()}`;

      const elemCount = elemCountByPage.get(key) ?? 0;
      const elemBadge = elemCount > 0 ? ` [${elemCount} elem]` : '';
      const label = `${badge}${elemBadge}\n${title}`;

      nodes.push({
        data: {
          id: key,
          label,
          fullTitle: title,
          fileId: node.getFileId(),
          pageNumber: node.getPage(),
          sheetId,
          elementCount: elemCount,
          nodeWidth: estimateLabelWidth(label),
          nodeHeight: estimateLabelHeight(label),
        },
        classes: key === currentPageKey ? 'current' : '',
      } as NodeDefinition);
    }

    const nodeIds = new Set(nodes.map(n => n.data.id));
    for (let i = 0; i < response.getEdgesList().length; i++) {
      const edge = response.getEdgesList()[i];
      const source = edge.getSource();
      const target = edge.getTarget();
      if (!source || !target) {
        console.warn(`[References] Edge ${i}: missing source/target`, { source, target });
        continue;
      }

      const sourceKey = `${source.getFileId()}-${source.getPage()}`;
      const targetKey = `${target.getFileId()}-${target.getPage()}`;

      if (!nodeIds.has(sourceKey) || !nodeIds.has(targetKey)) {
        console.warn(`[References] Edge ${i}: dangling ref`, { sourceKey, targetKey, nodeIds: [...nodeIds] });
        continue;
      }

      const edgeType = edge.getType();
      const color = EDGE_COLORS[edgeType] || '#9e9e9e';

      edges.push({
        data: {
          id: `e${i}`,
          source: sourceKey,
          target: targetKey,
          type: edgeType,
          edgeLabel: edgeType,
          color,
          reference: edge.getReference(),
          sourceLabel: edge.getSourceLabel(),
          targetLabel: edge.getTargetLabel(),
          weight: edge.getWeight(),
        },
      } as EdgeDefinition);
    }

    return { nodes, edges };
  }

  private extractFileId(page: ArchitecturalPlanPage): string {
    const path = page.getPageFolderPath();
    const match = path.match(/\/files\/(\d+)\/pages\//);
    return match ? match[1] : '1';
  }

  getElementIcon(elementType: string): string {
    const icons: Record<string, string> = {
      stair: 'stairs',
      elevator: 'elevator',
      window: 'window',
      door: 'door_front',
      'column-grid': 'grid_on',
      wall: 'fence',
      beam: 'straighten',
      foundation: 'foundation',
      roof: 'roofing',
      hvac: 'air',
      plumbing: 'plumbing',
      electrical: 'electric_bolt',
      'fire-protection': 'local_fire_department',
      'site-element': 'landscape',
      room: 'meeting_room',
      'deck-porch': 'deck',
      garage: 'garage',
    };
    return icons[elementType] || 'category';
  }

  navigateToPage(fileId: string, pageNumber: number): void {
    const planId = this.activePage?.getArchitecturalPlanId();
    if (planId) {
      const normalizedFileId = fileId === 'legacy' ? '1' : fileId;
      this.router.navigate(['/projects', planId, 'files', normalizedFileId, 'pages', pageNumber, 'references']);
    }
  }

  async rebuildGraph(): Promise<void> {
    const projectId = this.activePage?.getArchitecturalPlanId();
    if (!projectId || this.rebuilding) return;

    const SNACKBAR_DURATION_MS = 6000;
    this.rebuilding = true;
    this.cdr.detectChanges();
    try {
      const result = await this.planReviewerService.buildCrossReferenceGraph(projectId);
      if (result.success) {
        this.snackBar.open('Graph rebuild started — reload this tab when complete.', 'OK', {
          duration: SNACKBAR_DURATION_MS,
        });
      } else {
        this.snackBar.open(`Failed: ${result.message}`, 'Dismiss', {
          duration: SNACKBAR_DURATION_MS,
        });
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      this.snackBar.open(`Error: ${msg}`, 'Dismiss', { duration: SNACKBAR_DURATION_MS });
    } finally {
      this.rebuilding = false;
      this.cdr.detectChanges();
    }
  }

  async buildRegistry(): Promise<void> {
    const projectId = this.activePage?.getArchitecturalPlanId();
    if (!projectId || this.rebuildingRegistry) return;

    const SNACKBAR_DURATION_MS = 6000;
    this.rebuildingRegistry = true;
    this.cdr.detectChanges();
    try {
      const result = await this.planReviewerService.buildElementRegistry(projectId);
      if (result.success) {
        this.snackBar.open('Element registry build started — reload when complete.', 'OK', {
          duration: SNACKBAR_DURATION_MS,
        });
      } else {
        this.snackBar.open(`Failed: ${result.message}`, 'Dismiss', {
          duration: SNACKBAR_DURATION_MS,
        });
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      this.snackBar.open(`Error: ${msg}`, 'Dismiss', { duration: SNACKBAR_DURATION_MS });
    } finally {
      this.rebuildingRegistry = false;
      this.cdr.detectChanges();
    }
  }

  highlightElement(element: SharedElement | null): void {
    this.selectedElement = element;
    if (!this.cy) return;

    this.cy.nodes().removeClass('element-highlight');

    if (element) {
      for (const app of element.appearances) {
        const nodeId = `${app.fileId}-${app.page}`;
        const node = this.cy.getElementById(nodeId);
        if (node.length) {
          node.addClass('element-highlight');
        }
      }
    }
  }

  onPerspectiveChange(perspective: 'cross-refs' | 'elements' | 'combined'): void {
    this.graphPerspective = perspective;
    this.refreshRawData();
    this.elementsDrawerOpen = perspective !== 'cross-refs';
    if (this.cy) {
      if (perspective === 'elements') {
        this.cy.edges().style('opacity', 0.1);
      } else {
        this.cy.edges().style('opacity', 1);
      }
    }
  }

  toggleElementsDrawer(): void {
    this.elementsDrawerOpen = !this.elementsDrawerOpen;
  }

  private static readonly MIN_DRAWER_WIDTH = 280;
  private static readonly MAX_DRAWER_WIDTH = 700;

  onDrawerResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.cleanupResize();
    this.resizing = true;
    const startX = event.clientX;
    const startWidth = this.drawerWidth;

    this.resizeMoveHandler = (e: MouseEvent) => {
      const delta = startX - e.clientX;
      this.drawerWidth = Math.max(
        ReferencesComponent.MIN_DRAWER_WIDTH,
        Math.min(ReferencesComponent.MAX_DRAWER_WIDTH, startWidth + delta)
      );
      this.cdr.detectChanges();
    };
    this.resizeUpHandler = () => this.cleanupResize();

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', this.resizeMoveHandler);
    document.addEventListener('mouseup', this.resizeUpHandler);
  }

  private cleanupResize(): void {
    this.resizing = false;
    if (this.resizeMoveHandler) {
      document.removeEventListener('mousemove', this.resizeMoveHandler);
      this.resizeMoveHandler = null;
    }
    if (this.resizeUpHandler) {
      document.removeEventListener('mouseup', this.resizeUpHandler);
      this.resizeUpHandler = null;
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }

  fitGraph(): void {
    this.cy?.fit(undefined, 40);
  }

  centerOnCurrent(): void {
    if (!this.cy || !this.activePage) return;
    const key = `${this.extractFileId(this.activePage)}-${this.activePage.getPageNumber()}`;
    const node = this.cy.getElementById(key);
    if (node.length) {
      this.cy.animate({ center: { eles: node }, zoom: 1.5 } as any, { duration: 300 });
    }
  }

  onViewModeChange(mode: 'graph' | 'data'): void {
    this.viewMode = mode;
    if (mode === 'graph' && this.graphResponse) {
      this.scheduleRender();
    }
  }

  buildRawData(): string {
    if (!this.graphResponse) {
      return '';
    }

    if (this.graphPerspective === 'elements') {
      return JSON.stringify(this.buildRegistryRawData(), null, 2);
    }

    if (this.graphPerspective === 'combined') {
      return JSON.stringify(
        {
          cross_reference_graph: this.buildGraphRawData(this.graphResponse),
          building_element_registry: this.buildRegistryRawData(),
        },
        null,
        2
      );
    }

    return JSON.stringify(this.buildGraphRawData(this.graphResponse), null, 2);
  }

  private refreshRawData(): void {
    this.rawDataText = this.buildRawData();
    this.rawDataHtml = this.highlightJson(this.rawDataText);
  }

  private buildGraphRawData(response: GetCrossReferenceGraphResponse): Record<string, unknown> {
    return {
      version: response.getVersion(),
      built_at: response.getBuiltAt(),
      nodes: response.getNodesList().map(n => ({
        file_id: n.getFileId(),
        page: n.getPage(),
        sheet_id: n.getSheetId() || undefined,
        title: n.getTitle() || undefined,
      })),
      edges: response.getEdgesList().map(e => {
        const src = e.getSource();
        const tgt = e.getTarget();
        return {
          source: src ? { file_id: src.getFileId(), page: src.getPage() } : null,
          target: tgt ? { file_id: tgt.getFileId(), page: tgt.getPage() } : null,
          type: e.getType(),
          weight: e.getWeight(),
          reference: e.getReference() || undefined,
          detected_by: e.getDetectedBy() || undefined,
          source_label: e.getSourceLabel() || undefined,
          target_label: e.getTargetLabel() || undefined,
        };
      }),
    };
  }

  private buildRegistryRawData(): Record<string, unknown> {
    if (!this.registryResponse || !this.registryResponse.getExists()) {
      return {
        exists: false,
      };
    }

    return {
      version: this.registryResponse.getVersion(),
      built_at: this.registryResponse.getBuiltAt(),
      elements: this.registryResponse.getElementsList().map(el => ({
        element_id: el.getElementId(),
        canonical_name: el.getCanonicalName(),
        element_type: el.getElementType(),
        identity_class: el.getIdentityClass() || undefined,
        confidence: el.getConfidence() || undefined,
        matching_basis: el.getMatchingBasis() || undefined,
        appearances: el.getAppearancesList().map(app => ({
          file_id: app.getFileId(),
          page: app.getPage(),
          projection_type: app.getProjectionType(),
          description: app.getDescription() || undefined,
        })),
      })),
    };
  }

  private highlightJson(json: string): SafeHtml {
    const jsonLang = Prism.languages['json'];
    if (!jsonLang) {
      return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(json));
    }
    try {
      const html = Prism.highlight(json, jsonLang, 'json');
      const clean = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['span'],
        ALLOWED_ATTR: ['class'],
      });
      return this.sanitizer.bypassSecurityTrustHtml(clean);
    } catch (error) {
      console.error('[References] Prism.js highlighting failed:', error);
      return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(json));
    }
  }

  private escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}
